import type { GateId } from '@selogate/core'
import type { CachedGate } from '../../device-cache/snapshot'
import type { GateDetail } from './api'

/**
 * Look this gate up in the device-cache snapshot and render it as a `GateDetail`, or answer
 * `undefined` when this device has never listed it.
 *
 * The one entry point R5 step 4 wants: *is there anything to paint before the read lands*.
 */
export function cachedGateDetail(
  gates: readonly CachedGate[],
  gateId: GateId | string,
): GateDetail | undefined {
  const cached = gates.find((candidate) => candidate.gateId === gateId)
  return cached === undefined ? undefined : detailFromCache(cached)
}

/**
 * A cached gate rendered as a `GateDetail`.
 *
 * **It stopped being the offline read with R5 step 4, and that is the whole change here.** It was
 * private to `GateDetailScreen.tsx` and reached from one place — inside the catch, after the
 * network read had failed. It is now the *first paint* on both Gate Detail and the Access page,
 * with the network read arriving afterwards and winning. Nothing about the builder itself moved;
 * what moved is when it runs, which is why it lives in `screens/shared/` beside the API glue
 * rather than inside either screen.
 *
 * One builder, called from every path that needs one, because it used to be two byte-identical
 * inline literals and a field rename had to be applied to each — which is the shape a divergence
 * starts from. The absences are the same allow-list `toCachedGate` enforces: no coordinates, no
 * `bleConfigured`. **`managerCount` is D153's one deliberate manager-only
 * exception** — carried because it seeds `soleManager` on this exact offline path, and it is a
 * count, not location or billing detail.
 *
 * **R5 steps 1 and 2 map two more fields through, and neither is a new thing on disk.**
 * `subscription` is reconstructed from C2's `subscriptionRenewsAt` — `plan`/`status` were never
 * cached and are now optional on `GateDetail` for exactly this reason, and `renewsAt` is the only
 * member either screen reads. `shabbatEnabled` is the bit `toCachedGate` derives. Both were already
 * in the snapshot's own source data and were simply not read back; the omission is what made the
 * mode control's selected segment wrong on any render this builder produced.
 */
export function detailFromCache(cached: CachedGate): GateDetail {
  return {
    gateId: cached.gateId,
    defaultName: cached.defaultName,
    type: cached.type,
    status: cached.status,
    operatingState: cached.operatingState,
    location: { formatted: cached.locationFormatted ?? '', source: 'manual' },
    viewer: { role: cached.viewerRole, status: cached.viewerStatus, installer: false },
    createdAt: '',
    ...(cached.lastPosition === undefined ? {} : { lastPosition: cached.lastPosition }),
    ...(cached.lastSeenAt === undefined ? {} : { lastSeenAt: cached.lastSeenAt }),
    ...(cached.unseenExpired === undefined ? {} : { unseenExpired: cached.unseenExpired }),
    ...(cached.alias === undefined ? {} : { alias: cached.alias }),
    ...(cached.icon === undefined ? {} : { icon: cached.icon }),
    ...(cached.color === undefined ? {} : { color: cached.color }),
    ...(cached.sortOrder === undefined ? {} : { sortOrder: cached.sortOrder }),
    ...(cached.managerCount === undefined ? {} : { managerCount: cached.managerCount }),
    // R5 step 1 — C2 caches the date and nothing else, which is why `plan`/`status` are optional
    // on the type. `renewsAt` alone answers `expired`, `subscriptionDays` and
    // `accessForDetail`'s `subscription-expired`.
    ...(cached.subscriptionRenewsAt === undefined
      ? {}
      : { subscription: { renewsAt: cached.subscriptionRenewsAt } }),
    // R5 step 2 — absent stays absent (D135), so a gate cached before this shipped renders no
    // segment selected rather than the wrong one.
    ...(cached.shabbatEnabled === undefined ? {} : { shabbatEnabled: cached.shabbatEnabled }),
    // R7/D276 — absent stays absent, and `closeModeOf` is what turns that into `auto`. A gate
    // cached before this shipped therefore paints open-only, which is what it has always done.
    ...(cached.closeMode === undefined ? {} : { closeMode: cached.closeMode }),
  }
}
